import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ClipboardList, Loader2, Eye } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ReportSubjectPicker, EMPTY_MACHINE_INFO, hasMachineInfo } from "@/components/report/ReportSubjectPicker";
import { DatePicker } from "@/components/DatePicker";
import { PhotoUploader } from "@/components/PhotoUploader";
import { GpsConsent } from "@/components/geo/GpsConsent";
import { VoiceInput } from "@/components/VoiceInput";
import { AiTextAssist } from "@/components/AiTextAssist";
import { ReportSuccessDialog } from "@/components/ReportSuccessDialog";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { VoiceReportCard } from "@/components/voice/VoiceReportCard";
import { VoiceReportDialog, takeVoiceHandoff } from "@/components/voice/VoiceReportDialog";
import { hasPendingUploads, toImageRefs } from "@/lib/files";
import { useAuth, useFeatures } from "@/context/AuthContext";
import { todayISO } from "@/lib/format";
import api from "@/lib/api";

const Step = ({ n, title, desc, children }) => (
  <Card className="shadow-sm">
    <CardHeader className="pb-3">
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0B0D10] font-heading text-sm font-bold text-white dark:bg-white dark:text-[#0B0D10]">{n}</span>
        <div>
          <CardTitle className="font-heading text-base font-bold">{title}</CardTitle>
          {desc && <CardDescription className="mt-0.5">{desc}</CardDescription>}
        </div>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">{children}</CardContent>
  </Card>
);

export default function NyJobbrapport() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { map_enabled } = useFeatures();
  const [params] = useSearchParams();
  const preMachine = params.get("machine");
  const preCustomer = params.get("customer");
  const preVoice = params.get("voice");

  const [machines, setMachines] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [mode, setMode] = useState(preCustomer && !preMachine ? "customer" : "machine"); // machine | customer
  const [machineId, setMachineId] = useState(preMachine || "");
  const [customerId, setCustomerId] = useState(preCustomer || "");
  const [machineInfo, setMachineInfo] = useState(EMPTY_MACHINE_INFO);
  const [orderNumber, setOrderNumber] = useState("");
  const [date, setDate] = useState(todayISO());
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const [hoursSpent, setHoursSpent] = useState("");
  const [machineHours, setMachineHours] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voiceNote, setVoiceNote] = useState(null);
  const [gps, setGps] = useState(null); // {lat,lng,accuracy_m} – berre med samtykke
  const [voiceHandoff, setVoiceHandoff] = useState(null);

  useEffect(() => {
    api.get("/machines").then((r) => setMachines(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (preVoice === "handoff") {
      const h = takeVoiceHandoff();
      if (h) { setVoiceHandoff(h); setVoiceOpen(true); }
    } else if (preVoice === "1") {
      setVoiceOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Apply AI analysis of a voice note to the job form. */
  const applyVoice = (res, inc, note) => {
    if (inc.machine && res.machine?.matched_machine_id) { setMode("machine"); setMachineId(res.machine.matched_machine_id); }
    if (inc.title && res.title) setTitle(res.title);
    if (inc.description && res.description) setDescription((prev) => [prev.trim(), res.description].filter(Boolean).join("\n\n"));
    else if (inc.work && res.work_performed?.length) setDescription((prev) => [prev.trim(), res.work_performed.map((w) => `• ${w}`).join("\n")].filter(Boolean).join("\n"));
    if (inc.hours_spent && res.hours_spent != null) setHoursSpent(String(res.hours_spent));
    if (inc.hours && res.machine_hours != null) setMachineHours(String(res.machine_hours));
    if (inc.location && res.location) setLocation(res.location);
    if ((inc.follow_up || inc.defects) && (res.defects || res.follow_up)) {
      setNotes((prev) => [prev.trim(), res.defects, res.follow_up].filter(Boolean).join("\n"));
    }
    if (note?.file_id) setVoiceNote(note);
  };

  const selectedMachine = useMemo(() => machines.find((m) => m.id === machineId), [machines, machineId]);
  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === (mode === "machine" ? selectedMachine?.customer_id : customerId)),
    [customers, mode, selectedMachine, customerId]
  );

  const applyAi = (res) => {
    if (res.title && !title.trim()) setTitle(res.title);
    if (res.description) setDescription(res.description);
    else if (res.work_performed?.length) setDescription(res.work_performed.map((w) => `• ${w}`).join("\n"));
    if (res.defects) setNotes((prev) => [prev.trim(), res.defects].filter(Boolean).join("\n"));
  };

  const [previewPayload, setPreviewPayload] = useState(null);
  const uploading = hasPendingUploads(images);

  const validate = () => {
    if (mode === "machine" && !machineId) { toast.error("Vel ei maskin, eller byt til «Berre kunde»."); return false; }
    if (mode === "customer" && !customerId) { toast.error("Vel ein kunde."); return false; }
    if (!title.trim() && !description.trim()) { toast.error("Skriv ein tittel eller ei kort skildring av jobben."); return false; }
    if (uploading) { toast.info("Vent til bileta er ferdig lasta opp."); return false; }
    return true;
  };

  const buildPayload = () => ({
    report_type: "jobb",
    machine_id: mode === "machine" ? machineId : null,
    customer_id: mode === "customer" ? customerId : null,
    machine_info: mode === "customer" && hasMachineInfo(machineInfo) ? machineInfo : null,
    order_number: orderNumber.trim() || null,
    date,
    technician: user?.name,
    technician_title: user?.title,
    title: title.trim(),
    description: description.trim(),
    defects: notes.trim(),
    location: location.trim(),
    hours_spent: hoursSpent === "" ? null : Number(hoursSpent),
    machine_hours: mode === "machine" && machineHours !== "" ? Number(machineHours) : null,
    update_machine: true,
    images: toImageRefs(images),
    voice_note: voiceNote?.file_id ? voiceNote : null,
    gps: map_enabled && mode === "machine" ? gps || null : null,
  });

  const preview = () => { if (validate()) setPreviewPayload(buildPayload()); };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const res = await api.post("/reports", buildPayload());
      setSaved(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikkje lagre rapport.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5" data-testid="job-report-form">
      <button type="button" onClick={() => navigate(-1)} className="hidden h-9 items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground lg:inline-flex" data-testid="report-back-button">
        <ArrowLeft className="h-4 w-4" /> Tilbake
      </button>
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">Ny jobbrapport</h1>
        <p className="mt-1 text-sm text-muted-foreground">For reparasjonar og andre jobbar. Kort skildring, bilete og ferdig PDF.</p>
      </div>

      <VoiceReportCard note={voiceNote} onStart={() => setVoiceOpen(true)} onRemove={() => setVoiceNote(null)} testid="job-voice-card" />

      <Step n="1" title="Kva gjeld jobben?" desc="Knyt rapporten til ei registrert maskin, eller berre til kunden – med valfri maskininfo.">
        <ReportSubjectPicker
          machines={machines}
          customers={customers}
          mode={mode}
          onModeChange={setMode}
          machineId={machineId}
          onMachineChange={setMachineId}
          customerId={customerId}
          onCustomerChange={setCustomerId}
          machineInfo={machineInfo}
          onMachineInfoChange={setMachineInfo}
          onMachinesChanged={(m) => setMachines((list) => (list.some((x) => x.id === m.id) ? list.map((x) => (x.id === m.id ? { ...x, ...m } : x)) : [m, ...list]))}
          onCustomersChanged={(c) => setCustomers((list) => (list.some((x) => x.id === c.id) ? list.map((x) => (x.id === c.id ? { ...x, ...c } : x)) : [c, ...list]))}
          testid="job-report-subject"
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Dato</Label>
            <DatePicker value={date} onChange={setDate} clearable={false} testid="job-report-date-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Ordrenr <span className="font-normal text-muted-foreground">– for faktura</span></Label>
            <Input className="h-11" data-testid="job-report-order-number" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="t.d. 10245" autoComplete="off" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Stad</Label>
            <Input className="h-11" data-testid="job-report-location-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder={selectedCustomer?.address || "Anlegg / adresse"} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Timar brukt</Label>
            <Input className="h-11 tabular-nums" type="number" inputMode="decimal" step="0.5" data-testid="job-report-hours-spent-input" value={hoursSpent} onChange={(e) => setHoursSpent(e.target.value)} placeholder="t.d. 3.5" />
          </div>
          {mode === "machine" && (
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Timetal på maskina <span className="font-normal text-muted-foreground">– valfritt</span></Label>
              <Input className="h-11 tabular-nums" type="number" inputMode="decimal" data-testid="job-report-machine-hours-input" value={machineHours} onChange={(e) => setMachineHours(e.target.value)} placeholder={selectedMachine?.current_hours != null ? `Sist: ${selectedMachine.current_hours}` : ""} />
            </div>
          )}
        </div>
      </Step>

      <Step n="2" title="Kva vart gjort?" desc="Skriv kort – eller snakk inn og la AI rydde teksten (valfritt).">
        <div className="space-y-1.5">
          <Label className="text-sm font-semibold">Tittel</Label>
          <Input className="h-11 text-base" data-testid="job-report-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="t.d. Reparasjon av hydraulikkslange" />
        </div>
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label className="text-sm font-semibold">Skildring av arbeidet *</Label>
            <div className="flex items-center gap-2">
              <VoiceInput testid="job-description-voice" onText={(t) => setDescription((prev) => (prev ? `${prev} ${t}` : t))} />
              <AiTextAssist text={description} context="jobb" machineLabel={selectedMachine?.label} onApply={applyAi} testid="job-description-ai" />
            </div>
          </div>
          <Textarea data-testid="job-report-description" rows={6} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Kva vart gjort, kva delar vart bytta, resultat …" className="text-base" />
        </div>
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label className="text-sm font-semibold">Merknader / tilrådingar <span className="font-normal text-muted-foreground">– valfritt</span></Label>
            <VoiceInput testid="job-notes-voice" onText={(t) => setNotes((prev) => (prev ? `${prev} ${t}` : t))} />
          </div>
          <Textarea data-testid="job-report-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ting kunden bør følgje opp …" className="text-base" />
        </div>
      </Step>

      <Step n="3" title="Bilete" desc="Dokumenter før/etter. Bilete blir med i PDF-en.">
        <PhotoUploader value={images} onChange={setImages} testidPrefix="job-report-photo" />
        {map_enabled && mode === "machine" && <GpsConsent value={gps} onChange={setGps} testid="job-report-gps" />}
      </Step>

      <div className="sticky bottom-0 z-[120] -mx-4 border-t bg-background/95 px-4 pb-[calc(var(--safe-bottom)+12px)] pt-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:-mx-0 sm:rounded-[var(--radius)] sm:border sm:px-4 sm:pb-3" data-testid="report-sticky-bar">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 text-xs text-muted-foreground">
            <div className="truncate font-semibold text-foreground">
              {mode === "machine" ? (selectedMachine?.label || "Ingen maskin vald") : (selectedCustomer ? `${selectedCustomer.name}${hasMachineInfo(machineInfo) ? ` · ${[machineInfo.brand, machineInfo.type].filter(Boolean).join(" ")}` : ""}` : "Ingen kunde vald")}
            </div>
            <div>{title.trim() ? title : "Utan tittel"} · {images.length} bilete{orderNumber.trim() ? ` · Ordre ${orderNumber.trim()}` : ""}{uploading ? " · lastar opp …" : ""}</div>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button variant="ghost" onClick={() => navigate(-1)} className="hidden sm:inline-flex" data-testid="report-cancel-button">Avbryt</Button>
            <Button size="lg" variant="outline" onClick={preview} disabled={busy || uploading} data-testid="preview-job-report-button" aria-label="Førehandsvis PDF">
              <Eye /> <span className="hidden sm:inline">Førehandsvis</span>
            </Button>
            <Button size="lg" onClick={submit} disabled={busy || uploading} data-testid="save-job-report-button">
              {busy || uploading ? <Loader2 className="animate-spin" /> : <ClipboardList />} {uploading ? "Lastar opp …" : "Lagre rapport"}
            </Button>
          </div>
        </div>
      </div>

      <VoiceReportDialog
        open={voiceOpen}
        onOpenChange={(o) => { setVoiceOpen(o); if (!o) setVoiceHandoff(null); }}
        reportType="jobb"
        machines={machines}
        machineId={mode === "machine" ? machineId : ""}
        onApply={applyVoice}
        handoff={voiceHandoff}
      />
      <PdfPreviewDialog open={!!previewPayload} onOpenChange={(o) => !o && setPreviewPayload(null)} payload={previewPayload} title="Førehandsvising – jobbrapport" />
      <ReportSuccessDialog report={saved} onClose={() => { setSaved(null); navigate("/rapportar"); }} newPath="/rapportar/ny-jobb" />
    </div>
  );
}
